import React, { useState, useEffect, useRef } from 'react';
import { BLOG_POSTS } from '../../../utils/constants';
import { formatDate } from '../../../utils/helpers';
import { useRouting } from '../../../contexts/RoutingContext';
import type { BlogPost } from '../../../types';
import styles from '../AppContent.module.css';
import localStyles from './Blog.module.css';

// SVG icons for each post
const PostIcons: Record<string, React.FC<{ size?: number }>> = {
  'email-outreach-platform': ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 7.535V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7.535" />
      <path d="M2 7.535 12 13l10-5.465" />
      <path d="M4 5h16a2 2 0 0 1 2 2v0L12 13 2 7v0a2 2 0 0 1 2-2Z" />
      <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" />
    </svg>
  ),
  'linkedin-outreach-engine': ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6Z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  'grid-cybersecurity-research': ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="M9.5 12 11 13.5 14.5 10" />
    </svg>
  ),
};

const DefaultPostIcon: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

// Per-post theming
const POST_THEMES: Record<string, { gradient: string; accent: string }> = {
  'email-outreach-platform': {
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    accent: '#667eea',
  },
  'linkedin-outreach-engine': {
    gradient: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
    accent: '#0077b5',
  },
  'grid-cybersecurity-research': {
    gradient: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    accent: '#f12711',
  },
};

const DEFAULT_THEME = {
  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  accent: '#667eea',
};

const getTheme = (id: string) => POST_THEMES[id] || DEFAULT_THEME;
const getIcon = (id: string) => PostIcons[id] || DefaultPostIcon;

const getReadingTime = (text: string): number => {
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

// Enhanced markdown parser
const parseMarkdown = (markdown: string): string => {
  let html = markdown;

  // Remove the title (first # heading) since we display it separately
  html = html.replace(/^#\s+.+\n+/, '');

  // Code blocks with language
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
    const langLabel = lang ? `<span class="code-lang">${lang}</span>` : '';
    const langClass = lang ? ` class="language-${lang}"` : '';
    return `<div class="code-block-wrapper">${langLabel}<pre><code${langClass}>${escapeHtml(code.trim())}</code></pre></div>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr />');
  html = html.replace(/^\*\*\*$/gm, '<hr />');

  // Interactive tooltips: [[campaigns]] and [[personas]]
  html = html.replace(/\[\[campaigns?\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">campaigns</span><span class="tt-box"><strong>Our Active Campaigns</strong><span class="tt-row"><span class="tt-name">HVAC Service Companies</span><span class="tt-meta">active &middot; 150 contacts &middot; 37.1% reply rate</span></span><span class="tt-row"><span class="tt-name">Data Center Outreach</span><span class="tt-meta">active &middot; 50 contacts &middot; 18% reply rate</span></span><span class="tt-row"><span class="tt-name">RCx / Energy Consultants</span><span class="tt-meta">active &middot; 38 contacts &middot; 22.5% reply rate</span></span><span class="tt-row"><span class="tt-name">BAS Integrators</span><span class="tt-meta">active &middot; 36 contacts &middot; 7.3% reply rate</span></span><span class="tt-row"><span class="tt-name">Property Managers</span><span class="tt-meta">paused &middot; 33 contacts &middot; 15.4% reply rate</span></span></span></span>`
  );
  html = html.replace(/\[\[personas?\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">personas</span><span class="tt-box"><strong>Target Personas</strong><span class="tt-row"><span class="tt-name">Data Center Ops Leaders</span><span class="tt-meta">VPs, Directors of Operations at colocation &amp; enterprise DCs</span></span><span class="tt-row"><span class="tt-name">HVAC Service Owners</span><span class="tt-meta">Owners &amp; ops managers at commercial HVAC firms</span></span><span class="tt-row"><span class="tt-name">BAS Integrators</span><span class="tt-meta">Building automation system integrators &amp; controls engineers</span></span><span class="tt-row"><span class="tt-name">RCx Consultants</span><span class="tt-meta">Retrocommissioning &amp; energy consultants</span></span><span class="tt-row"><span class="tt-name">Property Managers</span><span class="tt-meta">Commercial real estate facilities directors</span></span></span></span>`
  );

  // More tooltips with real examples
  html = html.replace(/\[\[outreach strategies?\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">outreach strategies</span><span class="tt-box"><strong>Example Strategies</strong><span class="tt-row"><span class="tt-name">HVAC Companies</span><span class="tt-meta">Student researching maintenance pain points, low-pressure ask</span></span><span class="tt-row"><span class="tt-name">Data Centers</span><span class="tt-meta">Cooling efficiency angle, PUE optimization, energy cost savings</span></span><span class="tt-row"><span class="tt-name">BAS Integrators</span><span class="tt-meta">Channel partnership, bundling analytics with integration services</span></span></span></span>`
  );

  html = html.replace(/\[\[3-email sequence\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">3-email sequence</span><span class="tt-box"><strong>Email Sequence</strong><span class="tt-row"><span class="tt-name">Initial</span><span class="tt-meta">Day 0 &middot; personalized intro with research hook</span></span><span class="tt-row"><span class="tt-name">Follow-up 1</span><span class="tt-meta">Day 7 &middot; gentle nudge, new angle or use case</span></span><span class="tt-row"><span class="tt-name">Follow-up 2</span><span class="tt-meta">Day 14 &middot; final check-in, lower-pressure ask</span></span></span></span>`
  );

  html = html.replace(/\[\[personalization hooks?\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">personalization hooks</span><span class="tt-box"><strong>Example Hooks</strong><span class="tt-row"><span class="tt-name">Ongaro &amp; Sons</span><span class="tt-meta">Family-run since 1972, specializes in Marin County commercial HVAC</span></span><span class="tt-row"><span class="tt-name">CoreSite</span><span class="tt-meta">Silicon Valley GM overseeing 3 data centers, PUE below 1.3</span></span><span class="tt-row"><span class="tt-name">Vapor IO</span><span class="tt-meta">Edge DC founder, building micro data centers at cell towers</span></span></span></span>`
  );

  html = html.replace(/\[\[send window\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">send window</span><span class="tt-box"><strong>Send Window</strong><span class="tt-row"><span class="tt-name">Start</span><span class="tt-meta">7:00 AM Pacific</span></span><span class="tt-row"><span class="tt-name">End</span><span class="tt-meta">11:00 AM Pacific</span></span><span class="tt-row"><span class="tt-name">Daily cap</span><span class="tt-meta">23 emails / day</span></span><span class="tt-row"><span class="tt-name">Spacing</span><span class="tt-meta">Irregular gaps, one at a time</span></span></span></span>`
  );

  html = html.replace(/\[\[school emails?\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">school emails</span><span class="tt-box"><strong>Why .edu Emails</strong><span class="tt-row"><span class="tt-name">Domain</span><span class="tt-meta">akanteti@ucsc.edu</span></span><span class="tt-row"><span class="tt-name">Reputation</span><span class="tt-meta">University domains have high trust scores with email providers</span></span><span class="tt-row"><span class="tt-name">Deliverability</span><span class="tt-meta">Much less likely to land in spam vs a new custom domain</span></span></span></span>`
  );

  html = html.replace(/\[\[reply rate\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">reply rate</span><span class="tt-box"><strong>Reply Rates by Campaign</strong><span class="tt-row"><span class="tt-name">HVAC Service Companies</span><span class="tt-meta">37.1% &middot; 150 contacts</span></span><span class="tt-row"><span class="tt-name">University Facilities</span><span class="tt-meta">50% &middot; 52 contacts</span></span><span class="tt-row"><span class="tt-name">RCx / Energy Consultants</span><span class="tt-meta">22.5% &middot; 38 contacts</span></span><span class="tt-row"><span class="tt-name">Data Center Outreach</span><span class="tt-meta">21.1% &middot; 91 contacts</span></span><span class="tt-row"><span class="tt-name">Overall</span><span class="tt-meta">25% &middot; 241 sent &middot; 59 replies</span></span></span></span>`
  );

  html = html.replace(/\[\[dashboard\]\]/gi,
    `<span class="tt" tabindex="0"><span class="tt-word">dashboard</span><span class="tt-box"><strong>Dashboard Stats</strong><span class="tt-row"><span class="tt-name">Total Sent</span><span class="tt-meta">241 emails</span></span><span class="tt-row"><span class="tt-name">Response Rate</span><span class="tt-meta">25% &middot; 59 replies</span></span><span class="tt-row"><span class="tt-name">Active Contacts</span><span class="tt-meta">400 in system</span></span><span class="tt-row"><span class="tt-name">Bounced</span><span class="tt-meta">0 (auto-cleaned)</span></span></span></span>`
  );

  // Tables
  html = html.replace(/(?:^\|.+\|[ \t]*$\n?)+/gm, (match) => {
    const rows = match.trim().split('\n');
    if (rows.length < 2) return match;

    const isSeparator = (row: string) => {
      const cells = row.split('|').filter((_: string, i: number, arr: string[]) => i > 0 && i < arr.length - 1);
      return cells.length > 0 && cells.every((c: string) => /^[\s:-]+$/.test(c));
    };

    let separatorIdx = -1;
    for (let i = 0; i < rows.length; i++) {
      if (isSeparator(rows[i])) { separatorIdx = i; break; }
    }

    const parseRow = (row: string, tag: string) => {
      const cells = row.split('|').filter((_: string, i: number, arr: string[]) => i > 0 && i < arr.length - 1);
      return '<tr>' + cells.map((c: string) => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
    };

    let table = '<div class="table-wrapper"><table>';
    if (separatorIdx >= 0) {
      table += '<thead>';
      for (let i = 0; i < separatorIdx; i++) table += parseRow(rows[i], 'th');
      table += '</thead><tbody>';
      for (let i = separatorIdx + 1; i < rows.length; i++) {
        if (rows[i].trim()) table += parseRow(rows[i], 'td');
      }
      table += '</tbody>';
    } else {
      table += '<tbody>';
      rows.forEach((row: string) => { if (row.trim()) table += parseRow(row, 'td'); });
      table += '</tbody>';
    }
    table += '</table></div>';
    return table;
  });

  // Headers
  html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');

  // Blockquotes
  html = html.replace(/^>\s+(.+)$/gm, '<blockquote><p>$1</p></blockquote>');
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '');

  // Images (must come before bold/italic to protect underscores in filenames)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<figure><img src="$2" alt="$1" loading="lazy" /></figure>');

  // Links (must also come before italic to protect URLs with underscores)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Highlight link for Wattnest brand name
  html = html.replace(/(?<![a-zA-Z])Wattnest(?![a-zA-Z])/g, '<a href="https://www.wattnest.net" target="_blank" rel="noopener noreferrer" class="brand-link">Wattnest</a>');

  // Protect HTML attributes (src, href, alt) from bold/italic mangling
  const attrPlaceholders: string[] = [];
  html = html.replace(/(src|href|alt)="([^"]*)"/g, (match) => {
    attrPlaceholders.push(match);
    return `\x00ATTR${attrPlaceholders.length - 1}\x00`;
  });

  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');

  // Restore protected attributes
  html = html.replace(/\x00ATTR(\d+)\x00/g, (_, idx) => attrPlaceholders[parseInt(idx)]);

  // Unordered lists
  html = html.replace(/^-\s+(.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Ordered lists
  html = html.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');

  // Paragraphs
  html = html.replace(/\n\n+/g, '</p><p>');

  if (!html.startsWith('<')) {
    html = '<p>' + html + '</p>';
  }

  // Clean up block elements inside paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');
  const blocks = ['ul', 'ol', 'pre', 'h[1-6]', 'table', 'blockquote', 'figure', 'div', 'section'];
  blocks.forEach(tag => {
    html = html.replace(new RegExp(`<p>(<${tag}[> ])`, 'g'), '$1');
    const closeTag = tag.replace(/\[.*/, '');
    html = html.replace(new RegExp(`(</${closeTag}>)<\\/p>`, 'g'), '$1');
  });
  html = html.replace(/<p>(<hr\s*\/>)/g, '$1');
  html = html.replace(/(<hr\s*\/>)<\/p>/g, '$1');

  // Wrap h2-delimited sections in <section> tags with index classes for varied styling
  let sectionIdx = 0;
  html = html.replace(/<h2>/g, () => {
    sectionIdx++;
    const prefix = sectionIdx === 1 ? '' : '</section>';
    return `${prefix}<section class="blog-section blog-section-${sectionIdx}"><h2>`;
  });
  if (sectionIdx > 0) {
    html += '</section>';
  }

  return html;
};

const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export const Blog: React.FC = () => {
  const { getInitialSubPath, setSubPath } = useRouting();
  const initializedRef = useRef(false);

  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(() => {
    const subPathVal = getInitialSubPath('blog');
    if (subPathVal) {
      const found = BLOG_POSTS.find(post =>
        post.id === subPathVal || post.aliases?.includes(subPathVal)
      ) || null;
      // If found via alias, immediately normalize the subPath to canonical ID
      if (found && found.id !== subPathVal) {
        // Defer to after mount so RoutingContext is ready
        setTimeout(() => setSubPath('blog', found.id), 0);
      }
      return found;
    }
    return null;
  });
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setSubPath('blog', selectedPost?.id || null);
  }, [selectedPost, setSubPath]);

  useEffect(() => {
    if (selectedPost?.markdownFile) {
      setLoading(true);
      setError(null);

      fetch(selectedPost.markdownFile)
        .then(response => {
          if (!response.ok) throw new Error('Failed to load post');
          return response.text();
        })
        .then(content => {
          setMarkdownContent(content);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [selectedPost]);

  const handleBack = () => {
    setSelectedPost(null);
    setMarkdownContent('');
    setError(null);
  };

  // Article view
  if (selectedPost) {
    const theme = getTheme(selectedPost.id);
    const readingTime = markdownContent ? getReadingTime(markdownContent) : 0;

    return (
      <div className={styles.appContainer}>
        <div className={styles.toolbar}>
          <button className={styles.toolbarButton} onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 2 L4 8 L10 14 L10 10 L14 10 L14 6 L10 6 Z"/>
            </svg>
            Back to Posts
          </button>
        </div>

        <div className={`${styles.contentPanel} ${localStyles.articlePanel}`}>
          {/* Hero Header */}
          <div className={localStyles.hero} style={{ background: theme.gradient }}>
            <div className={localStyles.heroIcon}>{React.createElement(getIcon(selectedPost.id), { size: 36 })}</div>
            <h1 className={localStyles.heroTitle}>{selectedPost.title}</h1>
            <div className={localStyles.heroMeta}>
              <span>{formatDate(selectedPost.date)}</span>
              <span className={localStyles.heroDot}>&#183;</span>
              {readingTime > 0 && <span>{readingTime} min read</span>}
            </div>
            <div className={localStyles.heroTags}>
              {selectedPost.tags.map((tag) => (
                <span key={tag} className={localStyles.heroTag}>{tag}</span>
              ))}
            </div>
          </div>

          {/* Article Content */}
          <article className={localStyles.article}>
            <div className={localStyles.articleWrap}>
              <div className={localStyles.articleContent}>
                {loading && (
                  <div className={localStyles.loadingState}>
                    <div className={localStyles.spinner} />
                    <span>Loading article...</span>
                  </div>
                )}
                {error && (
                  <div className={localStyles.errorState}>
                    <span>Failed to load article: {error}</span>
                    <p>{selectedPost.content}</p>
                  </div>
                )}
                {!loading && !error && markdownContent && (
                  <div
                    className={localStyles.markdownBody}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(markdownContent) }}
                  />
                )}
                {!loading && !error && !markdownContent && !selectedPost.markdownFile && (
                  <p>{selectedPost.content}</p>
                )}
              </div>
            </div>
          </article>
        </div>
      </div>
    );
  }

  // Blog list view
  return (
    <div className={styles.appContainer}>
      <div className={styles.toolbar}>
        <button className={styles.toolbarButton}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 3h12v1H2zM2 6h12v1H2zM2 9h8v1H2z"/>
          </svg>
          All Posts
        </button>
      </div>

      <div className={styles.contentPanel}>
        <div className={localStyles.blogHeader}>
          <h2 className={localStyles.blogTitle}>Blog</h2>
          <p className={localStyles.blogSubtitle}>
            Engineering deep dives, automation builds, and lessons from the trenches.
          </p>
        </div>

        <div className={localStyles.posts}>
          {BLOG_POSTS.map((post) => {
            const theme = getTheme(post.id);
            return (
              <article
                key={post.id}
                className={`${localStyles.postCard} ${post.upcoming ? localStyles.postCardUpcoming : ''}`}
                onClick={() => !post.upcoming && setSelectedPost(post)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !post.upcoming) setSelectedPost(post);
                }}
              >
                <div
                  className={localStyles.postAccent}
                  style={{ background: post.upcoming ? '#999' : theme.gradient }}
                >
                  <span className={localStyles.postIcon}>{React.createElement(getIcon(post.id), { size: 28 })}</span>
                </div>
                <div className={localStyles.postBody}>
                  <h3 className={localStyles.postTitle}>
                    {post.title}
                    {post.upcoming && <span className={localStyles.upcomingBadge}>[upcoming]</span>}
                  </h3>
                  <div className={localStyles.postMeta}>
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <p className={localStyles.postExcerpt}>{post.excerpt}</p>
                  <div className={localStyles.postTags}>
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className={localStyles.postTag}
                        style={{ borderColor: theme.accent, color: theme.accent }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={localStyles.postArrow}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="#999">
                    <path d="M6 3l5 5-5 5V3z"/>
                  </svg>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className={styles.statusBar}>
        {BLOG_POSTS.length} posts
      </div>
    </div>
  );
};

export default Blog;
